import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFragment } from "@apollo/client/react";
import {
  Box,
  Button,
  EmptyState,
  Icon,
  ScrollArea,
  Tabs,
  TabsPanel,
  Text,
} from "@salestrak/ui";
import {
  WorkflowStageFragmentDoc,
  type IssueRow,
} from "#app/lib/data";
import type { IssueViewStage, IssueViewTab } from "./issue-view-types";
import { IssueViewHeader } from "./issue-view-header";
import { IssueViewSummary } from "./issue-view-summary";
import { ActivityList } from "./activity/activity-list";
import { ActivityListSkeleton } from "./activity/activity-list-skeleton";
import { ActivityProvider } from "./activity/activity-context";
import { NoteComposer } from "./activity/note-composer";
import {
  useActivityContextValue,
  type UseActivityContextValueResult,
} from "#app/hooks/issue-view/use-activity-context-value";
import { useIssueViewData } from "#app/hooks/issue-view/use-issue-view-data";
import { useIssueMediaItems } from "#app/hooks/issue-view/use-issue-media-items";
import { IssueMediaTab } from "./issue-media-tab";
import { IssueViewCapabilitiesProvider } from "./issue-view-capabilities";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import type { Omittable } from "@salestrak/ui";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

// Tabs/TabsPanel own their own `min-h-0` now, so what is left here is the two
// decisions only this screen can make: the tab shell claims the body's free
// space, and the panel — not a ScrollArea inside it — is the scroller.
// `gap-0`: the panel meets the strip's hairline directly, so the layout
// editor's sunken field reaches it with no background sliver between.
const TABS_ROOT = "flex-1 gap-0";
const TAB_PANEL = "overflow-y-auto";

type IssueBodyProps = Omittable<{
  /** When set, view mode. When null, create mode (relies on surrounding CreateIssueProvider). */
  issue: IssueRow | null;
  focusNoteId?: string | null;
  /** Land the activity feed on its first unread comment. */
  focusUnread?: boolean;
  /** Scroll to + highlight this custom field row on the summary tab. */
  focusCustomFieldId?: string | null;
  /** Focus the summary's title input — a fresh nonce per request so a
   *  repeat request on the already-open issue re-fires. */
  focusTitleNonce?: number;
  activeTab: IssueViewTab;
  setActiveTab: (tab: IssueViewTab) => void;
  contentMaxWidth: number | undefined;
  isMobile: boolean;
  /** Render composer docked below list (desktop view). Sheet passes composer via the drawer footer. */
  inlineComposer?: boolean;
  /** Shared activity context (sheet reuses same ref in footer composer). */
  activityContext?: UseActivityContextValueResult;
}>;

export function IssueBody(props: IssueBodyProps) {
  return props.issue ? <ViewBody {...props} issue={props.issue} /> : <CreateBody {...props} />;
}

// Thin wrapper that decides whether to compute its own activity context or
// use one provided by the parent (mobile sheet does the latter — and we MUST
// not call useActivityContextValue twice for the same issue, since each call
// spins up its own useQuery + fetchMore, doubling network requests).
function ViewBody(props: IssueBodyProps & { issue: IssueRow }) {
  if (props.activityContext) {
    return <ViewBodyInner {...props} activityContext={props.activityContext} />;
  }
  return <ViewBodyWithOwnContext {...props} />;
}

function ViewBodyWithOwnContext(props: IssueBodyProps & { issue: IssueRow }) {
  const ownContext = useActivityContextValue(
    props.issue.issueId,
    props.focusNoteId,
    true,
    props.focusUnread,
  );
  return <ViewBodyInner {...props} activityContext={ownContext} />;
}

function ViewBodyInner({
  issue,
  activeTab,
  setActiveTab,
  contentMaxWidth,
  inlineComposer,
  activityContext,
  focusCustomFieldId,
  focusTitleNonce,
}: IssueBodyProps & { issue: IssueRow; activityContext: UseActivityContextValueResult }) {
  const { issue: liveIssue, stage, assignees, values, mediaCount: serverMediaCount, workflowId, viewerAccessLevel, viewerCanEdit, address, issueTypes, customFields } =
    useIssueViewData(issue.issueId);
  const resolvedIssue = (liveIssue as IssueRow | null) ?? issue;
  const { stageMap } = useWorkspaceData();
  const cachedStageResult = useFragment({
    fragment: WorkflowStageFragmentDoc,
    fragmentName: "WorkflowStage",
    from: { __typename: "Stage", stageId: resolvedIssue.stageId },
  });
  const cachedStage = cachedStageResult.complete
    ? (cachedStageResult.data as IssueViewStage)
    : null;
  // A move returns `stageId` without the nested `stage`, which then names
  // the old stage until a refetch.
  const resolvedStage =
    (stage?.stageId === resolvedIssue.stageId ? stage : null) ??
    cachedStage ??
    stageMap.get(resolvedIssue.stageId) ??
    null;
  const resolvedAssignees = assignees ?? resolvedIssue.assignees;
  const resolvedValues =
    values === undefined ? resolvedIssue.values : values;
  const resolvedWorkflowId =
    workflowId === undefined ? resolvedIssue.workflowId : workflowId;
  const resolvedAddress =
    address === undefined ? resolvedIssue.address : address;
  const resolvedViewerAccessLevel =
    viewerAccessLevel === undefined
      ? issue.viewerAccessLevel
      : viewerAccessLevel;
  const resolvedViewerCanEdit =
    viewerCanEdit === undefined ? issue.viewerCanEdit : viewerCanEdit;

  const { value, loading, hasActivities, notes } = activityContext;
  const mediaItems = useIssueMediaItems(customFields, resolvedValues, notes);
  // The Attachments tab is PHOTOS-flagged, but note attachments stay in the
  // media list whatever the flag says, so an issue that has any keeps the tab
  // in a flag-off workspace.
  //
  // Two separate decisions, and they answer differently while the flags are
  // still in flight. SHOWING the tab waits for `ready`: a tab that appears
  // and then vanishes is worse than one that arrives a beat late, and a
  // flag-off workspace must never flash an Attachments tab at all. REDIRECTING
  // away from a requested media tab also waits for `ready`, so a deep link or
  // a widget's fallback navigation is not thrown away before anyone knows
  // whether the tab exists.
  const { ready, hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);
  const showMediaTab = ready && (photosEnabled || mediaItems.length > 0);
  const resolveTab = useCallback(
    (tab: IssueViewTab): IssueViewTab =>
      tab === "media" && ready && !showMediaTab ? "summary" : tab,
    [ready, showMediaTab],
  );
  const currentTab = resolveTab(activeTab);
  // Tab label: the server count arrives with the issue query, so it shows
  // immediately; once the notes feed has loaded, the client aggregation is
  // the live source (it tracks field edits without a refetch).
  const mediaCount = loading ? (serverMediaCount ?? 0) : mediaItems.length;

  // Media-tab quick links: jump to the field row on summary (reusing the
  // deep-link focus flow) or scroll to the note on activity. The local focus
  // clears when leaving summary so a later jump to the same field re-fires.
  const [mediaFieldFocus, setMediaFieldFocus] = useState<string | null>(null);
  const handleTabChange = useCallback(
    (requested: IssueViewTab) => {
      if (resolveTab(requested) !== "summary") setMediaFieldFocus(null);
      // Store the request, not its resolution: `currentTab` re-resolves every
      // render, so a tab that is unavailable now — flags still in flight, note
      // media not loaded — is honored once it opens instead of being lost to a
      // one-time coercion written into parent state.
      setActiveTab(requested);
    },
    [resolveTab, setActiveTab],
  );
  const handleNavigateToField = useCallback(
    (customFieldId: string) => {
      setMediaFieldFocus(customFieldId);
      setActiveTab("summary");
    },
    [setActiveTab],
  );
  const { scrollToNote } = value;
  const handleNavigateToNote = useCallback(
    (noteId: string, parentNoteId?: string | null) => {
      setActiveTab("activity");
      // scrollToNote retries across frames, so it survives the tab content
      // mounting after this call.
      void scrollToNote?.(noteId, parentNoteId);
    },
    [setActiveTab, scrollToNote],
  );

  return (
    <IssueViewCapabilitiesProvider
      level={resolvedViewerAccessLevel}
      canEdit={resolvedViewerCanEdit}
    >
    <Box direction="column" fill="flex" minHeight={0} position="relative">
      <Tabs
        className={TABS_ROOT}
        value={currentTab}
        onValueChange={(v) => {
          trackEvent({
            context: AnalyticsContext.Issue,
            event: IssueEvent.SwitchDetailTab,
            data: { tab: v as IssueViewTab, via: "strip" },
          });
          handleTabChange(v as IssueViewTab);
        }}
      >
        <IssueViewHeader
          activeTab={activeTab}
          issue={resolvedIssue}
          mediaCount={mediaCount}
          onSelectTab={(tab) => {
            trackEvent({
              context: AnalyticsContext.Issue,
              event: IssueEvent.SwitchDetailTab,
              data: { tab, via: "more_menu" },
            });
            handleTabChange(tab);
          }}
          showMediaTab={showMediaTab}
          stage={resolvedStage}
          contentMaxWidth={contentMaxWidth}
        />
        <TabsPanel className={TAB_PANEL} value="summary">
          <IssueViewSummary
            issue={resolvedIssue}
            stage={resolvedStage}
            workflowId={resolvedWorkflowId}
            address={resolvedAddress}
            assignees={resolvedAssignees}
            issueTypes={issueTypes}
            values={resolvedValues}
            customFields={customFields}
            contentMaxWidth={contentMaxWidth}
            focusCustomFieldId={mediaFieldFocus ?? focusCustomFieldId}
            focusTitleNonce={focusTitleNonce}
            onNavigateTab={handleTabChange}
            activity={value}
            activityLoading={loading}
            notes={notes}
            mediaItems={mediaItems}
          />
        </TabsPanel>
        <TabsPanel className={TAB_PANEL} value="activity">
          {loading && !hasActivities ? (
            // The feed's own silhouette, in the feed's column, so the entries
            // land on the bars. Delayed: a reopened issue answers from cache.
            <Box align="center" paddingY="md">
              <Box fill="width" maxWidth={contentMaxWidth} paddingX="sm">
                <ActivityListSkeleton delayed />
              </Box>
            </Box>
          ) : (
            <ActivityProvider value={value}>
              {hasActivities ? (
                <Box direction="column" fill="flex" minHeight={0}>
                  <ActivityList issueId={resolvedIssue.issueId} contentMaxWidth={contentMaxWidth} />
                </Box>
              ) : (
                <Box flex={1} align="center" justify="center" padding="xl">
                  <EmptyState
                    size="sm"
                    media="circle"
                    icon="activity"
                    title="No activity yet"
                    description="Comments and changes on this Project appear here."
                  />
                </Box>
              )}
              {inlineComposer && (
                <Box align="center">
                  <Box fill="width" maxWidth={contentMaxWidth} paddingX="sm" paddingBottom="sm">
                    <NoteComposer issueId={resolvedIssue.issueId} />
                  </Box>
                </Box>
              )}
            </ActivityProvider>
          )}
        </TabsPanel>
        {showMediaTab ? (
          <TabsPanel className={TAB_PANEL} value="media">
            <Box direction="column" fill="flex" minHeight={0}>
              <IssueMediaTab
                items={mediaItems}
                issueId={resolvedIssue.issueId}
                issueName={resolvedIssue.name}
                onNavigateToField={handleNavigateToField}
                onNavigateToNote={handleNavigateToNote}
              />
            </Box>
          </TabsPanel>
        ) : null}
      </Tabs>
    </Box>
    </IssueViewCapabilitiesProvider>
  );
}

function CreateBody({ contentMaxWidth, isMobile }: IssueBodyProps) {
  const { submit, submitAttemptCount, submitted, canSubmit, missingRequiredCount, draft } = useCreateIssueMode()!;
  const [barDismissed, setBarDismissed] = useState(false);

  useEffect(() => {
    if (submitAttemptCount > 0) setBarDismissed(false);
  }, [submitAttemptCount]);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) { hasMountedRef.current = true; return; }
    setBarDismissed(true);
  }, [draft.name, draft.values]);

  const dismissBar = useCallback(() => setBarDismissed(true), []);
  const showMissingBar = submitted && !canSubmit && !barDismissed;

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <Box direction="column" fill="flex" minHeight={0} position="relative">
        <IssueViewHeader />
        {isMobile ? (
          <IssueViewSummary contentMaxWidth={contentMaxWidth} />
        ) : (
          <ScrollArea>
            <IssueViewSummary contentMaxWidth={contentMaxWidth} />
          </ScrollArea>
        )}
        {showMissingBar && (
          // Two elements: the floating row must not eat clicks on the fields
          // under it, while the bar inside it stays clickable.
          <Box
            pointerEvents="none"
            position="absolute"
            bottom={isMobile ? 8 : 16}
            left={0}
            right={0}
            zIndex={50}
            paddingX="md"
            align="center"
          >
            {/* The whole bar submits, and it contains a real dismiss button —
                so the bar cannot be a button element itself. */}
            <Box
              pointerEvents="auto"
              surface="popover"
              border
              borderColor="destructive"
              rounded="lg"
              paddingX="md"
              paddingY="sm"
              cursor="pointer"
              role="button"
              tabIndex={0}
              onClick={submit}
            >
              <Box direction="row" gap="sm" align="center">
                <Icon name="alert-circle" size="xs" color="destructive" />
                <Text size="sm" weight="semibold">
                  {missingRequiredCount} required field{missingRequiredCount !== 1 ? "s" : ""} missing
                </Text>
                <Button variant="ghost" size="icon-xs" onClick={(e) => { e.stopPropagation(); dismissBar(); }}>
                  <Icon name="x" size="xs" />
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
